"use client";
import { withArticle } from "@/lib/utils";

import { useState, useTransition } from "react";
import Link from "next/link";
import Image from "next/image";
import type { JobType, TradePricing } from "@/lib/pricing-data";
import type { LocationData } from "@/lib/regional-multipliers";
import { PostcodeInput } from "@/app/components/PostcodeInput";
import { isValidPostcodeFormat } from "@/lib/postcode";
import {
  searchNearbyTradespeople,
  type NearbyTradesperson,
} from "@/app/actions/search";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function fmt(n: number) {
  return `£${Math.round(n).toLocaleString()}`;
}

function SeasonalChart({ demand }: { demand: number[] }) {
  const max = Math.max(...demand);
  return (
    <div className="flex items-end gap-1.5 sm:gap-2 h-32">
      {demand.map((val, i) => {
        const pct = max > 0 ? (val / max) * 100 : 0;
        const isPeak = val >= max * 0.85;
        const isLow = val <= max * 0.5;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div
              className={`w-full rounded-t-sm transition-all ${
                isPeak
                  ? "bg-copper"
                  : isLow
                    ? "bg-sage-light/50"
                    : "bg-sage"
              }`}
              style={{ height: `${Math.max(pct, 8)}%` }}
              title={`${MONTHS[i]}: ${Math.round(val * 100)}% demand`}
            />
            <span className="text-[10px] text-text-muted">{MONTHS[i]}</span>
          </div>
        );
      })}
    </div>
  );
}

function ComparisonBar({ multiplier }: { multiplier: number }) {
  const pct = Math.min(Math.max(((multiplier - 0.6) / 0.9) * 100, 2), 98);
  const label =
    multiplier >= 1.1
      ? "More expensive"
      : multiplier <= 0.9
        ? "Cheaper than average"
        : "Around the national average";
  const color =
    multiplier >= 1.1
      ? "bg-copper"
      : multiplier <= 0.9
        ? "bg-success"
        : "bg-sage";

  return (
    <div>
      <div className="flex justify-between text-xs text-text-muted mb-1.5">
        <span>Cheapest</span>
        <span>National avg</span>
        <span>Most expensive</span>
      </div>
      <div className="relative h-3 bg-stone rounded-full overflow-hidden">
        <div
          className="absolute top-0 left-1/2 w-px h-full bg-text-muted/40"
          style={{ left: `${((1.0 - 0.6) / 0.9) * 100}%` }}
        />
        <div
          className={`absolute top-0 left-0 h-full rounded-full ${color} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-sm font-medium text-text-body mt-1.5">{label}</p>
    </div>
  );
}

export function PricePageClient({
  pricing,
  location,
  demand,
}: {
  pricing: TradePricing;
  location: LocationData;
  demand: number[];
}) {
  const [selectedJob, setSelectedJob] = useState<JobType>(
    pricing.jobTypes[0]
  );
  const [scopeIdx, setScopeIdx] = useState(1);
  const [postcode, setPostcode] = useState("");
  const [results, setResults] = useState<NearbyTradesperson[]>([]);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const m = location.multiplier;
  const scope = selectedJob.scopes[scopeIdx];
  const estLow = Math.round(
    (selectedJob.labourLow + selectedJob.materialsLow) * scope.multiplier * m
  );
  const estHigh = Math.round(
    (selectedJob.labourHigh + selectedJob.materialsHigh) * scope.multiplier * m
  );

  function handleSearch() {
    if (!isValidPostcodeFormat(postcode)) {
      setSearchError("Please enter a valid UK postcode.");
      return;
    }
    setSearchError(null);
    startTransition(async () => {
      const res = await searchNearbyTradespeople(postcode, pricing.tradeSlug);
      if (res.error) {
        setSearchError(res.error);
      } else {
        setResults(res.results);
        setSearched(true);
      }
    });
  }

  return (
    <>
      {/* Cost Calculator */}
      <section className="bg-white rounded-md border border-border-light p-5 md:p-6 mb-8">
        <h2 className="font-serif text-xl font-semibold text-charcoal mb-4">
          Cost Calculator
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-body mb-2">
              Job type
            </label>
            <select
              value={selectedJob.slug}
              onChange={(e) => {
                const job = pricing.jobTypes.find(
                  (j) => j.slug === e.target.value
                );
                if (job) {
                  setSelectedJob(job);
                  setScopeIdx(1);
                }
              }}
              className="w-full border border-border rounded-sm px-3 py-2.5 text-sm bg-white text-text-body focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
            >
              {pricing.jobTypes.map((j) => (
                <option key={j.slug} value={j.slug}>
                  {j.name}
                </option>
              ))}
            </select>

            <label className="block text-sm font-medium text-text-body mt-4 mb-2">
              Scope of work
            </label>
            <div className="flex flex-col gap-2">
              {selectedJob.scopes.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setScopeIdx(i)}
                  className={`text-left px-4 py-3 rounded-sm border text-sm font-medium transition-all ${
                    scopeIdx === i
                      ? "border-forest bg-forest/5 text-forest"
                      : "border-border-light bg-white text-text-body hover:border-sage-light"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <div className="bg-sage-pale rounded-md p-5 text-center">
              <p className="text-text-muted text-sm mb-1">
                Estimated cost in {location.name}
              </p>
              <p className="text-3xl font-bold text-forest">
                {fmt(estLow)} - {fmt(estHigh)}
              </p>
              <p className="text-text-muted text-xs mt-2">
                {selectedJob.name} ({scope.name.toLowerCase()})
              </p>
              <p className="text-text-muted text-xs mt-1">
                Typical duration: {selectedJob.durationDays}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Price Breakdown for all job types */}
      <section className="mb-8">
        <h2 className="font-serif text-xl font-semibold text-charcoal mb-4">
          {pricing.tradeName} Prices in {location.name}
        </h2>

        <div className="space-y-4">
          {pricing.jobTypes.map((job) => {
            const localLabourLow = Math.round(job.labourLow * m);
            const localLabourHigh = Math.round(job.labourHigh * m);
            const localMaterialsLow = Math.round(job.materialsLow * m);
            const localMaterialsHigh = Math.round(job.materialsHigh * m);
            const localTotal =
              localLabourLow +
              localMaterialsLow +
              (localLabourHigh + localMaterialsHigh);

            const labourPct =
              localTotal > 0
                ? Math.round(
                    ((localLabourLow + localLabourHigh) / localTotal) * 100
                  )
                : 50;

            return (
              <div
                key={job.slug}
                className="bg-white rounded-md border border-border-light p-5"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <h3 className="font-semibold text-charcoal">{job.name}</h3>
                  <span className="text-lg font-bold text-forest">
                    {fmt(localLabourLow + localMaterialsLow)} -{" "}
                    {fmt(localLabourHigh + localMaterialsHigh)}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-text-muted mb-1">Labour</p>
                    <p className="text-sm font-medium text-text-body">
                      {fmt(localLabourLow)} - {fmt(localLabourHigh)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted mb-1">Materials</p>
                    <p className="text-sm font-medium text-text-body">
                      {fmt(localMaterialsLow)} - {fmt(localMaterialsHigh)}
                    </p>
                  </div>
                </div>

                {/* Labour vs Materials bar */}
                <div className="mb-3">
                  <div className="flex h-2.5 rounded-full overflow-hidden bg-stone">
                    <div
                      className="bg-forest rounded-l-full"
                      style={{ width: `${labourPct}%` }}
                    />
                    <div
                      className="bg-sage-light rounded-r-full"
                      style={{ width: `${100 - labourPct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-text-muted mt-1">
                    <span>Labour {labourPct}%</span>
                    <span>Materials {100 - labourPct}%</span>
                  </div>
                </div>

                <p className="text-xs text-text-muted">
                  Typical duration: {job.durationDays}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* National Average Comparison */}
      <section className="bg-white rounded-md border border-border-light p-5 md:p-6 mb-8">
        <h2 className="font-serif text-xl font-semibold text-charcoal mb-4">
          How {location.name} Compares
        </h2>
        <ComparisonBar multiplier={m} />
        <p className="text-sm text-text-muted mt-3">
          Our model for {location.name} is{" "}
          {m >= 1.01
            ? `${Math.round((m - 1) * 100)}% above`
            : m <= 0.99
              ? `${Math.round((1 - m) * 100)}% below`
              : "in line with"}{" "}
          the base estimate. This is a location assumption, not a measured local quote average.
        </p>
      </section>

      {/* Seasonal Demand */}
      <section className="bg-white rounded-md border border-border-light p-5 md:p-6 mb-8">
        <h2 className="font-serif text-xl font-semibold text-charcoal mb-4">
          Illustrative Seasonality for {pricing.tradeName} Work
        </h2>
        <SeasonalChart demand={demand} />
        <p className="text-sm text-text-muted mt-4">
          {(() => {
            const peakMonth = demand.indexOf(Math.max(...demand));
            const lowMonth = demand.indexOf(Math.min(...demand));
            return `Our seasonal model peaks in ${MONTHS[peakMonth]} and is lowest in ${MONTHS[lowMonth]}. This is not live demand data or a forecast of prices or availability.`;
          })()}
        </p>
      </section>

      {/* Cost Factors */}
      <section className="bg-white rounded-md border border-border-light p-5 md:p-6 mb-8">
        <h2 className="font-serif text-xl font-semibold text-charcoal mb-4">
          What Affects the Price
        </h2>
        <div className="space-y-6">
          {pricing.jobTypes.map((job) => (
            <div key={job.slug}>
              <h3 className="text-sm font-semibold text-charcoal mb-2">
                {job.name}
              </h3>
              <ul className="space-y-1.5">
                {job.costFactors.map((factor, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-text-body"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-sage mt-1.5 shrink-0" />
                    {factor}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Find Tradespeople */}
      <section className="bg-white rounded-md border border-border-light p-5 md:p-6">
        <h2 className="font-serif text-xl font-semibold text-charcoal mb-2">
          {pricing.tradeName} Profiles Near {location.name}
        </h2>
        <p className="text-text-muted text-sm mb-4">
          Find trusted, reviewed {pricing.tradeName.toLowerCase()} professionals
          near you. Tradesfolk is completely free to use.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1">
            <PostcodeInput
              value={postcode}
              onChange={setPostcode}
              placeholder="Enter your postcode"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={isPending}
            className="px-6 py-2.5 bg-forest text-white font-semibold rounded-sm text-sm transition-colors hover:bg-forest-light disabled:opacity-50 whitespace-nowrap"
          >
            {isPending ? "Searching..." : "Find Tradespeople"}
          </button>
        </div>

        {searchError && (
          <p className="text-error text-sm mb-4">{searchError}</p>
        )}

        {searched && results.length === 0 && !searchError && (
          <p className="text-text-muted text-sm">
            No {pricing.tradeName.toLowerCase()} profiles found in that area
            yet.{" "}
            <Link href={`/post-job?trade=${encodeURIComponent(pricing.tradeSlug)}`} className="text-forest no-underline hover:underline">
              Post a job
            </Link>{" "}
            and tradespeople will come to you.
          </p>
        )}

        {results.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((tp) => (
              <Link
                key={tp.user_id}
                href={`/tradesperson/${tp.user_id}`}
                className="block bg-warm-bg rounded-sm p-4 no-underline transition-all hover:shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
              >
                <div className="flex items-center gap-3 mb-2">
                  {tp.photo_url ? (
                    <Image
                      src={tp.photo_url}
                      alt=""
                      width={40}
                      height={40}
                      className="rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-forest text-white text-sm font-bold flex items-center justify-center">
                      {tp.first_name?.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-charcoal">
                      {tp.first_name} {tp.last_name?.charAt(0)}.
                    </p>
                    {tp.business_name && (
                      <p className="text-xs text-text-muted">
                        {tp.business_name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-text-muted">
                  {tp.avg_rating > 0 && (
                    <span className="flex items-center gap-1">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="text-copper"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      {tp.avg_rating.toFixed(1)} ({tp.review_count})
                    </span>
                  )}
                  {tp.distance_miles != null && (
                    <span>{tp.distance_miles.toFixed(1)} miles</span>
                  )}
                  {tp.years_experience != null && tp.years_experience > 0 && (
                    <span>{tp.years_experience} yrs exp</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Link
            href={`/find/${pricing.tradeSlug}/${location.slug}`}
            className="inline-block px-6 py-3 bg-forest text-white font-semibold rounded-sm no-underline text-sm text-center transition-colors hover:bg-forest-light"
          >
            Find {withArticle(pricing.tradeName)} in {location.name}
          </Link>
          <Link
            href={`/post-job?trade=${encodeURIComponent(pricing.tradeSlug)}`}
            className="inline-block px-6 py-3 border border-forest text-forest font-semibold rounded-sm no-underline text-sm text-center transition-colors hover:bg-forest/5"
          >
            Post a Job for Free
          </Link>
        </div>
      </section>
    </>
  );
}
